import { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { cn } from '../../lib/cn';
import { formatHebrewRelative } from '../../lib/relativeTime';
import { BotSubNav } from './BotSubNav';
import {
  BOT_STATES,
  BOT_STATE_LABELS_HE,
  SERVICE_LABELS_HE,
  type BotConversationRow,
  type BotState,
  type ConversationFilters,
  type ServiceSlug,
} from './bot.schema';
import { useBotConversations } from './botQueries';
import styles from './bot.module.css';

function statePillClass(state: BotState): string {
  if (state === 'GREETING' || state === 'AWAITING_PAIN' || state === 'AWAITING_NAME') return styles.statePillActive ?? '';
  if (state === 'IDLE_AFTER_HANDOFF' || state === 'HUMAN_TAKEOVER') return styles.statePillHuman ?? '';
  if (state === 'DORMANT') return styles.statePillDormant ?? '';
  if (state === 'CLOSED') return styles.statePillClosed ?? '';
  return '';
}

function scoreClass(score: number | null): string {
  if (score === null) return '';
  if (score >= 70) return styles.scorePillHot ?? '';
  if (score >= 40) return styles.scorePillWarm ?? '';
  return styles.scorePillCold ?? '';
}

function ConversationRow({ conv }: { conv: BotConversationRow }) {
  return (
    <Link to={`/bot/conversations/${conv.id}`} className={styles.row}>
      <span className={styles.rowPhone}>
        <bdi dir="ltr">{conv.phone}</bdi>
      </span>
      <div>
        <div className={styles.rowName}>{conv.full_name ?? 'ללא שם'}</div>
        <div className={styles.rowMeta}>
          {conv.service_slug ? SERVICE_LABELS_HE[conv.service_slug] : '—'}
          {conv.business_type ? ` · ${conv.business_type}` : ''}
        </div>
      </div>
      <span className={cn(styles.statePill, statePillClass(conv.state))}>
        {BOT_STATE_LABELS_HE[conv.state]}
      </span>
      {conv.lead_score !== null ? (
        <span className={cn(styles.scorePill, scoreClass(conv.lead_score))}>
          <span className="num">{String(conv.lead_score)}</span>
        </span>
      ) : (
        <span className={styles.rowMeta}>—</span>
      )}
      <span className={styles.rowTime}>
        {formatHebrewRelative(conv.last_message_at)}
        <ChevronRight size={14} aria-hidden="true" style={{ marginInlineStart: 4, verticalAlign: 'middle', transform: 'scaleX(-1)' }} />
      </span>
    </Link>
  );
}

export function ConversationsPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;

  const [search, setSearch] = useState('');
  const [stateFilters, setStateFilters] = useState<BotState[]>([]);
  const [serviceFilters, setServiceFilters] = useState<(ServiceSlug | null)[]>([]);
  const [showDormant, setShowDormant] = useState(false);

  const filters = useMemo<ConversationFilters>(
    () => ({ search, states: stateFilters, serviceSlugs: serviceFilters, showDormant }),
    [search, stateFilters, serviceFilters, showDormant],
  );

  const { data: conversations = [], isLoading } = useBotConversations(orgId, filters);

  function toggleState(s: BotState) {
    setStateFilters((prev) => (prev.includes(s) ? prev.filter((v) => v !== s) : [...prev, s]));
  }
  function toggleService(s: ServiceSlug) {
    setServiceFilters((prev) => (prev.includes(s) ? prev.filter((v) => v !== s) : [...prev, s]));
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>שיחות הבוט</h1>
          <p className={styles.subtitle}>כל שיחת WhatsApp פעילה: מצב, דירוג, ליד, פעילות אחרונה.</p>
        </div>
      </header>

      <BotSubNav />

      <div className={styles.filterBar}>
        <input
          type="search"
          className={styles.search}
          placeholder="חיפוש לפי טלפון, שם, סוג עסק"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />
        <div className={styles.chipGroup}>
          {BOT_STATES.filter((s) => s !== 'DORMANT').map((s) => (
            <button
              key={s}
              type="button"
              className={cn(styles.chip, stateFilters.includes(s) && styles.chipActive)}
              onClick={() => {
                toggleState(s);
              }}
            >
              {BOT_STATE_LABELS_HE[s]}
            </button>
          ))}
        </div>
        <div className={styles.chipGroup}>
          {(Object.keys(SERVICE_LABELS_HE) as ServiceSlug[]).map((s) => (
            <button
              key={s}
              type="button"
              className={cn(styles.chip, serviceFilters.includes(s) && styles.chipActive)}
              onClick={() => {
                toggleService(s);
              }}
            >
              {SERVICE_LABELS_HE[s]}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={cn(styles.chip, showDormant && styles.chipActive)}
          onClick={() => {
            setShowDormant((v) => !v);
          }}
        >
          הצג רדומים
        </button>
      </div>

      {isLoading ? (
        <p className={styles.notice}>טוען שיחות...</p>
      ) : conversations.length === 0 ? (
        <p className={styles.empty}>אין שיחות פעילות. הבוט ממתין לפנייה ראשונה.</p>
      ) : (
        <div className={styles.list}>
          {conversations.map((c) => (
            <ConversationRow key={c.id} conv={c} />
          ))}
        </div>
      )}
    </div>
  );
}
